<script setup lang="ts">
import { AdSlot, AppShell } from '@it-tools/shell'
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import MetricsPanel from './components/MetricsPanel.vue'
import ScreenForm from './components/ScreenForm.vue'
import { computeMetrics, type ScreenSpec } from './model/ppi'
import { DEFAULT_SPEC, readLocationSpec, replaceLocationSpec } from './model/urlState'

const { t } = useI18n()

const spec = ref<ScreenSpec>(DEFAULT_SPEC)
const shareNotice = ref(false)

const metrics = computed(() => computeMetrics(spec.value))

// A shared link restores the screen; a malformed one silently falls back to the
// default so the visitor always lands on a working calculator.
onMounted(() => {
  const restored = readLocationSpec()
  if (restored) spec.value = restored
})

// Only a usable screen goes in the URL — half-typed input would produce links that
// restore nothing.
watch(spec, (value) => {
  if (computeMetrics(value)) replaceLocationSpec(value)
})

async function share(): Promise<void> {
  if (metrics.value) replaceLocationSpec(spec.value)
  try {
    await navigator.clipboard.writeText(window.location.href)
    shareNotice.value = true
    setTimeout(() => (shareNotice.value = false), 2000)
  } catch {
    // Clipboard access can be denied; the URL bar already holds the link.
  }
}
</script>

<template>
  <AppShell tool-name-key="tools.ppiCalculator">
    <template #controls>
      <span v-if="shareNotice" class="notice" role="status">{{ t('common.copied') }}</span>
    </template>

    <div class="layout">
      <div class="layout__main">
        <section class="controls">
          <ScreenForm v-model="spec" />
          <div class="controls__actions">
            <button type="button" @click="spec = DEFAULT_SPEC">{{ t('common.reset') }}</button>
            <button type="button" :disabled="!metrics" @click="share()">
              {{ t('common.share') }}
            </button>
          </div>
        </section>

        <AdSlot placement="leaderboard" />

        <MetricsPanel :metrics="metrics" :unit="spec.unit" />

        <p class="hint">{{ t('hint.usage') }}</p>
      </div>

      <AdSlot placement="sidebar" />
    </div>
  </AppShell>
</template>

<style scoped>
.layout {
  display: flex;
  gap: var(--space-6);
  align-items: flex-start;
}

.layout__main {
  flex: 1;
  min-width: 0;
}

.controls {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  margin-bottom: var(--space-4);
}

.controls__actions {
  display: flex;
  gap: var(--space-2);
}

button {
  padding: var(--space-2) var(--space-4);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
  color: var(--color-text);
  font: inherit;
  font-size: var(--text-sm);
  cursor: pointer;
}

button:hover:not(:disabled) {
  background: var(--color-surface-raised);
  border-color: var(--color-border-strong);
}

button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.hint {
  margin-top: var(--space-3);
  font-size: var(--text-sm);
  color: var(--color-text-muted);
}

.notice {
  font-size: var(--text-sm);
  color: var(--color-success);
}
</style>
